import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { PrivatesAppointmentsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useAppointmentDelete = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async (appointmentId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(
        PrivatesAppointmentsApi,
        accessToken,
      );
      return service.apiAppointmentsIdDelete({
        id: appointmentId,
      });
    },
    onSuccess: async (_data, deletedAppointmentId) => {
      queryClient.removeQueries({
        queryKey: [QueryKeys.Appointments, deletedAppointmentId],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Appointments],
      });
      toast({
        title: "Private lesson deleted",
        description: "Private lesson has been deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete appointment";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return {
    mutate,
    isPending,
  };
};
